// --------------------------------------------------------------
// Created On: 2023-02-19
// Author: Zachary Thomas
//
// Last Modified: 2026-04-24
// Modified By: Dimitra Weinstein
//
// Copyright 2024 - 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React from "react";
import userHasCrossCompanyAccess from "../../../../utilities/userHasCrossCompanyAccess";
import useReduxDispatch from "src/hooks/useReduxDispatch";
import useReduxSelector from "../../../../hooks/useReduxSelector";
import { setCurrentUser } from "src/redux/features/currentUserSlice";
import { Link, useNavigate } from "react-router-dom";

// A single found asset in an asset list.
export default function SearchAssetListRow(props: Props): Component {
  const currentUser = useReduxSelector((state) => state.currentUser);
  const dispatch = useReduxDispatch();
  const navigate = useNavigate();

  // Go to asset page.
  function goToAsset(assetId: number, companyId: number, companyName: string): void {
    // If we are a super admin, switch to the specified company.
    if (userHasCrossCompanyAccess()) {
      dispatch(
        setCurrentUser({
          userId: currentUser.userId,
          userName: currentUser.userName,
          emailAddress: currentUser.emailAddress,
          originalCompanyId: currentUser.originalCompanyId,
          originalCompanyName: currentUser.originalCompanyName,
          originalPermissions: currentUser.originalPermissions,
          companyId: companyId,
          companyName: companyName,
          userCompanyPath: currentUser.userCompanyPath,
          isCompanyHidden: false,
          permissions: currentUser.permissions,
          isPackager: currentUser.isPackager,
          mfaEnabled: currentUser.mfaEnabled,
        })
      );
    }
    // Go to the selected assets details page.
    navigate(`/asset/${assetId}/data`);
  }

  return (
    <tr className="selectable-container" onClick={() => goToAsset(props.assetId, props.companyId, props.companyName)}>
      {props.crossCompany && (
        <td data-test="search-result-asset-company">
          <Link to={`/asset/${props.assetId}/data`} className="router-link">
            {props.companyName}
          </Link>
        </td>
      )}
      <td data-test="search-result-asset-name">
        <Link to={`/asset/${props.assetId}/data`} className="router-link">
          {props.name}
        </Link>
      </td>
      <td data-test="search-result-asset-nickname">
        <Link to={`/asset/${props.assetId}/data`} className="router-link">
          {props.nickname}
        </Link>
      </td>
      <td data-test="search-result-product-type" className="d-none d-xl-table-cell">
        <Link to={`/asset/${props.assetId}/data`} className="router-link">
          {props.productType}
        </Link>
      </td>
      <td data-test="search-result-product-model" className="d-none d-lg-table-cell">
        <Link to={`/asset/${props.assetId}/data`} className="router-link">
          {props.productModel}
        </Link>
      </td>
      <td data-test="search-result-product-identifier" className="d-none d-md-table-cell">
        <Link to={`/asset/${props.assetId}/data`} className="router-link">
          {props.productIdentifier}
        </Link>
      </td>
      <td data-test="search-result-device-type" className="d-none d-md-table-cell">
        <Link to={`/asset/${props.assetId}/data`} className="router-link">
          {props.deviceType}
        </Link>
      </td>
      <td data-test="search-result-device-identifier" className="d-none d-md-table-cell">
        <Link to={`/asset/${props.assetId}/data`} className="router-link">
          {props.deviceIdentifier}
        </Link>
      </td>
    </tr>
  );
}

interface Props {
  assetId: number;
  name: string;
  nickname: string;
  productType: string;
  productModel: string;
  productIdentifier: string;
  companyId: number;
  companyName: string;
  crossCompany: boolean;
  deviceType: string;
  deviceIdentifier: string;
}
